<template>
	<div class="custom-pagination" :class="{ 'is-background': background, 'is-small': small }">
	  <!-- 总条数 -->
	  <span v-if="showTotal" class="custom-pagination__total">
		共 {{ total }} 条
	  </span>
  
	  <!-- 每页条数选择 -->
	  <span v-if="showSizes" class="custom-pagination__sizes">
		<select 
		  :disabled="disabled"
		  :value="innerPageSize"
		  @change="handleSizeChange($event.target.value)"
		>
		  <option 
			v-for="size in pageSizes"
			:key="size"
			:value="size"
		  >
			{{ size }} 条/页
		  </option>
		</select>
	  </span>
  
	  <!-- 上一页 -->
	  <button
		v-if="showPrev"
		class="custom-pagination__prev"
		:disabled="disabled || isFirstPage"
		@click="goToPage(innerCurrentPage - 1)"
	  >
		&lt;
	  </button>
  
	  <!-- 页码列表 -->
	  <template v-if="showPager">
		<template v-for="(page, index) in pagerPages" :key="index">
		  <button
			v-if="page === '...'"
			class="custom-pagination__more"
			disabled
		  >
			...
		  </button>
		  <button
			v-else
			class="custom-pagination__number"
			:class="{ 'is-active': page === innerCurrentPage }"
			:disabled="disabled"
			@click="goToPage(page)"
		  >
			{{ page }}
		  </button>
		</template>
	  </template>
  
	  <!-- 下一页 -->
	  <button
		v-if="showNext"
		class="custom-pagination__next"
		:disabled="disabled || isLastPage"
		@click="goToPage(innerCurrentPage + 1)"
	  >
		&gt;
	  </button>
  
	  <!-- 跳转 -->
	  <span v-if="showJumper" class="custom-pagination__jumper">
		前往
		<input
		  type="number"
		  :min="1"
		  :max="totalPages"
		  :disabled="disabled"
		  @keyup.enter="handleJump"
		/>
		页
	  </span>
	</div>
  </template>
  
  <script setup>
  import { computed, ref, watch } from 'vue'
  
  const props = defineProps({
	total: { type: Number, required: true },
	pageSizes: { type: Array, default: () => [10, 20, 30, 50] },
	currentPage: { type: Number, default: 1 },
	pageSize: { type: Number, default: 10 },
	layout: { type: String, default: 'total, sizes, prev, pager, next, jumper' },
	small: Boolean,
	disabled: Boolean,
	background: Boolean
  })
  
  const emit = defineEmits([
	'update:currentPage',
	'update:pageSize',
	'current-change',
	'size-change'
  ])
  
  // 内部维护的状态
  const innerCurrentPage = ref(props.currentPage)
  const innerPageSize = ref(props.pageSize)
  
  // 计算属性
  const totalPages = computed(() => Math.ceil(props.total / innerPageSize.value))
  const isFirstPage = computed(() => innerCurrentPage.value <= 1)
  const isLastPage = computed(() => innerCurrentPage.value >= totalPages.value)
  
  // 布局显示控制
  const layoutParts = computed(() => props.layout.split(',').map(p => p.trim()))
  const showTotal = computed(() => layoutParts.value.includes('total'))
  const showSizes = computed(() => layoutParts.value.includes('sizes'))
  const showPrev = computed(() => layoutParts.value.includes('prev'))
  const showNext = computed(() => layoutParts.value.includes('next'))
  const showPager = computed(() => layoutParts.value.includes('pager'))
  const showJumper = computed(() => layoutParts.value.includes('jumper'))
  
  // 生成页码列表
  const pagerPages = computed(() => {
	const current = innerCurrentPage.value
	const pageCount = totalPages.value
	const showPages = 7 // 显示页码数量
	const pages = []
  
	if (pageCount <= showPages) {
	  for (let i = 1; i <= pageCount; i++) {
		pages.push(i)
	  }
	} else {
	  const left = Math.max(2, current - 2)
	  const right = Math.min(pageCount - 1, current + 2)
	  
	  pages.push(1)
	  
	  if (left > 2) pages.push('...')
	  
	  for (let i = left; i <= right; i++) {
		pages.push(i)
	  }
	  
	  if (right < pageCount - 1) pages.push('...')
	  
	  pages.push(pageCount)
	}
  
	return pages
  })
  
  // 方法
  const goToPage = (page) => {
	page = Math.max(1, Math.min(page, totalPages.value))
	if (page !== innerCurrentPage.value) {
	  innerCurrentPage.value = page
	  emit('update:currentPage', page)
	  emit('current-change', page)
	}
  }
  
  const handleSizeChange = (size) => {
	const newSize = Number(size)
	innerPageSize.value = newSize
	emit('update:pageSize', newSize)
	emit('size-change', newSize)
	goToPage(1) // 切换每页条数后回到第一页
  }
  
  const handleJump = (e) => {
	const page = parseInt(e.target.value)
	if (!isNaN(page)) goToPage(page)
  }
  
  // 监听props变化
  watch(() => props.currentPage, val => innerCurrentPage.value = val)
  watch(() => props.pageSize, val => innerPageSize.value = val)
  </script>
  
  <style scoped>
  .custom-pagination {
	display: flex;
	align-items: center;
	gap: 8px;
	font-size: 14px;
  }
  
  .custom-pagination.is-small {
	font-size: 12px;
  }
  
  .custom-pagination.is-background button {
	background-color: #f4f4f5;
	border: 1px solid #d8dce5;
	border-radius: 4px;
  }
  
  .custom-pagination button {
	min-width: 30px;
	height: 28px;
	padding: 0 4px;
	border: none;
	background: none;
	cursor: pointer;
  }
  
  .custom-pagination.is-small button {
	min-width: 24px;
	height: 24px;
  }
  
  .custom-pagination button:disabled {
	color: #c0c4cc;
	cursor: not-allowed;
	background-color: transparent;
  }
  
  .custom-pagination button.is-active {
	color: #409eff;
	font-weight: bold;
  }
  
  .custom-pagination__number.is-active.is-background {
	background-color: #409eff;
	color: white;
  }
  
  .custom-pagination__jumper input {
	width: 50px;
	margin: 0 6px;
	padding: 3px;
	text-align: center;
  }
  
  .custom-pagination__sizes select {
	padding: 3px 6px;
	margin-left: 6px;
  }
  </style>